import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldCheck, ShieldAlert, FileDown, Loader2 } from 'lucide-react';
import { signatures, API_BASE } from '../lib/api';
import { fmtStamp } from '../components/LiveClock';

const money = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function PoVerify() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    signatures.shared(token).then(setData).catch((e) => setError(e?.response?.data?.detail || 'Link not found'));
  }, [token]);

  if (error) {
    return (
      <div data-testid="po-verify-error" className="max-w-md mx-auto px-6 py-16 text-center">
        <ShieldAlert className="w-12 h-12 mx-auto text-[#B71C1C]"/>
        <h1 className="font-heading font-black text-2xl mt-3">{error}</h1>
      </div>
    );
  }
  if (!data) return <div className="py-16 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-ink/40"/></div>;

  const allValid = data.signatures.length > 0 && data.signatures.every((s) => s.valid);
  return (
    <div data-testid="po-verify-page" className="max-w-2xl mx-auto px-4 md:px-6 pt-8 pb-36 md:pb-10">
      <div className="text-[10px] uppercase tracking-[0.2em] font-black text-ink/60">Purchase order · shared via BharatCart</div>
      <h1 data-testid="po-verify-ord" className="font-heading font-black text-3xl">{data.ord_id}</h1>
      <div className="text-sm text-ink/60 mt-1">Buyer <b className="text-ink">{data.buyer}</b> → Seller <b className="text-ink">{data.seller}</b> · {fmtStamp(data.created_at)}</div>

      <div data-testid="po-verify-status" className={`mt-5 rounded-2xl p-4 flex items-center gap-3 ${allValid ? 'bg-[#E8F5E9] text-[#1B5E20]' : 'bg-[#FFEBEE] text-[#B71C1C]'}`}>
        {allValid ? <ShieldCheck className="w-8 h-8 shrink-0"/> : <ShieldAlert className="w-8 h-8 shrink-0"/>}
        <div>
          <div className="font-black">{allValid ? 'Signatures verified' : 'Signature check failed'}</div>
          <div className="text-xs">{allValid ? 'This PO has not been changed since it was signed.' : 'The PO was changed after signing — confirm with the sender.'}</div>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-2">
        {data.signatures.map((s) => (
          <div key={s.role} data-testid={`po-verify-sig-${s.role}`} className="rounded-xl border-2 border-ink/10 bg-white p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-black text-ink/50">{s.role === 'seller' ? 'Seller' : 'Buyer'}</div>
            <div className="font-bold">{s.name}</div>
            <div className="text-[11px] text-ink/60">{fmtStamp(s.signed_at, true)} · {s.methods.join(' + ')}</div>
            <div className="font-mono text-[9px] text-ink/50 break-all mt-1">{s.hash}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-xl border-2 border-ink/10 bg-white divide-y divide-ink/5">
        {data.items.map((it, i) => (
          <div key={i} className="px-3 py-2 flex items-center gap-2 text-sm">
            <span className="flex-1 min-w-0 truncate">{it.name}</span>
            <span className="text-ink/60">{it.qty} {it.unit}</span>
            <span className="font-bold w-24 text-right">{money(it.line_total)}</span>
          </div>
        ))}
        <div className="px-3 py-2 flex justify-between font-black"><span>Subtotal</span><span data-testid="po-verify-subtotal">{money(data.subtotal)}</span></div>
      </div>

      {data.has_pdf && (
        <a data-testid="po-verify-download" href={`${API_BASE}/po/share/${token}/pdf`} target="_blank" rel="noreferrer"
          className="btn-primary w-full mt-5 !py-3"><FileDown className="w-4 h-4"/>Open signed PDF</a>
      )}
    </div>
  );
}
